/**
 * A reusable "image plus accordion" section with two large parallax title words. Its default copy
 * is the "Ignite Your Career With Ryerson" section of the Careers page, and other sections reuse it
 * as their layout with their own copy.
 *
 * Layout:
 * - **From `lg` up:** on the left, the active card's `desktopImage` with `title1` and `title2`
 *   rotated to read upwards along its right edge, each moving with scroll
 *   (`react-scroll-parallax`); on the right, an accordion with one tab per card, one open at a
 *   time. Opening a tab slides that card's image up over the previous one.
 * - **Below `lg`:** a plain list of cards, each with its `mobileImage`, its title linked to
 *   `actionUrl`, and its description. The title words aren't shown; the caller's `AllSections`
 *   accordion tab carries the section title.
 *
 * Rendered by:
 * - `src/pages/about-us/careers/index.tsx` (copy `./Content.tsx` → `IgniteYourContent`, offsets
 *   `./Content.tsx` → `LanguageParallax`);
 * - `components/Shared/RyersonDifference/RyersonDifferenceSection.tsx` (the Ryerson Difference
 *   section; its header lists the pages that show it);
 * - `components/MetalResources/References/References.tsx` (desktop only);
 * - `components/Products/Characteristics.tsx` (desktop only, on the Contentful product pages;
 *   every card shares one image, and it sets `firstImageOnly`).
 * `components/Shared/RyersonDifference/Content2.tsx`,
 * `components/MetalResources/MetalResourcesLandingRyerson.tsx` and
 * `components/MetalResources/MetalResourcesLandingSTS.tsx` import only the content type.
 *
 * Rules a change must keep:
 * - `content.cards` must have at least one card: the first render reads `cards[0].desktopImage`.
 * - The title positions (`title*Left`, `title*Parallax`) depend on the length of the title words,
 *   so the callers pass their own values, per language where the copy differs (`./Content.tsx` →
 *   `LanguageParallax`). A change to the font size, rotation or position of the titles here moves
 *   them in every caller.
 *
 * `./STS/STSIgniteYourCareer.tsx` is the Southern Tool Steel copy of this file with different
 * spacing; a layout change here isn't carried over to it.
 */
import React from "react";
import { IgniteYourContent } from "./Content";
import { useApplication } from "@ryerson/frontend.application";
import { Media } from "@ryerson/frontend.next";
import { ContentSection } from "@ryerson/frontend.layout";
import { Typography } from "@ryerson/frontend.typography";
import { Accordion, AccordionTab } from "@ryerson/frontend.expansion";
import { ButtonLink, Link } from "@ryerson/frontend.navigation";
import { ParallaxProvider, Parallax } from "react-scroll-parallax";
import { CSSEffect } from "parallax-controller";
import { useTheme } from "@ryerson/frontend.theme";
import styled from "@emotion/styled";
import { css } from "@emotion/react";

/** One card: an accordion tab from `lg` up, a stacked card below it. */
export type IgniteYourCareerCard = {
	/** Tab header and card heading. */
	title: string;
	description: string;
	/** Desktop button label. The button is drawn only when both this and `actionUrl` are set. */
	actionLabel?: string;
	/** Desktop button target, and the link on the mobile card heading. */
	actionUrl?: string;
	/** Image above the mobile card. */
	mobileImage: string;
	/** Left-hand image from `lg` up, shown while this card's tab is open. */
	desktopImage: string;
};

/** One language's section copy. */
export type IgniteYourCareerContent = {
	/**
	 * First title word, drawn rotated on the image from `lg` up; also the `alt` text of every card
	 * image.
	 */
	title1: string;
	/** Second title word, drawn beside `title1`. */
	title2: string;
	/** Label for callers' sub-navigation; not read by this component. */
	navLabel?: string;
	/** Paragraph above the accordion, from `lg` up only. */
	description?: string;
	/** At least one card (see the file header). */
	cards: IgniteYourCareerCard[];
};

/**
 * Props for the section. The `title*` props place the two rotated title words, which are drawn
 * from `lg` up only. The plain variants apply from `xl` up and the `Smaller` variants between `lg`
 * and `xl`.
 */
export type IgniteYourCareerProps = {
	/**
	 * Section copy. Defaults to `./Content.tsx` → `IgniteYourContent` in the visitor's language.
	 */
	content?: IgniteYourCareerContent;
	/**
	 * Theme background of the section from `lg` up (below `lg` the section is always `primary`);
	 * also the right panel's colour unless `rightBackground` is set. Default `"secondary"`.
	 */
	type?: "primary" | "secondary" | "tertiary";
	/**
	 * Turns off the slide-in transition when the open tab changes. For callers whose cards all
	 * share one `desktopImage` (`components/Products/Characteristics.tsx`).
	 */
	firstImageOnly?: boolean;
	/** CSS colour of the right (accordion) panel, overriding the `type` background. */
	rightBackground?: string;
	/** `react-scroll-parallax` `translateY` range `[start, end]` for `title1`, from `xl` up. */
	title1Parallax?: string[];
	/** `translateY` range for `title2`, from `xl` up. */
	title2Parallax?: string[];
	/** CSS `left` of `title1` within the title strip on the image's right edge, from `xl` up. */
	title1Left?: string;
	/** CSS `left` of `title2`, from `xl` up. */
	title2Left?: string;
	/** CSS `left` of `title1` between `lg` and `xl`. */
	title1LeftSmaller?: string;
	/** CSS `left` of `title2` between `lg` and `xl`. */
	title2LeftSmaller?: string;
	/** `translateY` range for `title1` between `lg` and `xl`. */
	title1ParallaxSmaller?: string[];
	/** `translateY` range for `title2` between `lg` and `xl`. */
	title2ParallaxSmaller?: string[];
	/**
	 * Font size of both title words from `xl` up, in place of `100px`. Between `lg` and `xl` they
	 * are always `60px`.
	 */
	customFontSize?: string;
};

const Contain = styled.div`
	width: 100%;
	display: block;
`;

const Left = styled.div`
	position: absolute;
	white-space: nowrap;
	right: 51.4%;
	width: 48.6%;
	max-width: 700px;
	height: auto;
	max-height: 850px;
`;

const ImageContainer = styled.div`
	width: 100%;
	display: block;
	position: relative;
	background-position: top left;
	background-repeat: no-repeat;
	overflow: hidden;
`;

const Right = styled.div`
	position: absolute;
	left: 48.6%;
	width: 51.4%;
	max-width: 740px;
	padding-right: 140px;
	padding-top: 100px;
	overflow: hidden;
	box-sizing: border-box;
	${(props: any) => {
		const { theme } = props;
		return css`
			@media (max-width: ${theme.breakpoints.xl}) {
				padding-right: 20px;
				padding-top: 20px;
			}
		`;
	}}
`;

const AccordionContainer = styled.div`
	margin-left: auto;
	width: 65%;
	${(props: any) => {
		const { theme } = props;
		return css`
			@media (max-width: ${theme.breakpoints.xl}) {
				width: 85%;
			}
		`;
	}}
`;

const ButtonLinkContainer = styled.div`
	display: block;
	width: 100%;
	padding-top: 30px;
	padding-bottom: 80px;
	${(props: any) => {
		const { theme } = props;
		return css`
			@media (max-width: ${theme.breakpoints.xl}) {
				padding-bottom: 30px;
			}
		`;
	}}
`;

const TitleContainer = styled.div`
	position: absolute;
	right: -18px;
	top: 0;
	height: 100%;
	width: 150px;
	z-index: 2;
`;

const Title1 = styled.div`
	position: absolute;
	transform: rotate(270deg);
	top: 330px;
	white-space: pre;
	font-size: 100px;
	letter-spacing: -0.04em;
	${(props: any) => {
		const { theme } = props;
		return css`
			font-family: ${theme.typography.fontFamilies.headings};
			color: ${theme.colors.primary.white};
			@media only screen and (max-width: ${theme.breakpoints.xl}) {
				top: 195px;
				font-size: 60px;
			}
		`;
	}}
`;
const Title2 = styled.div`
	position: absolute;
	white-space: pre;
	transform: rotate(270deg);
	top: 330px;
	font-size: 100px;
	letter-spacing: -0.04em;
	${(props: any) => {
		const { theme } = props;
		return css`
			font-family: ${theme.typography.fontFamilies.headings};
			color: ${theme.colors.primary.white};
			@media only screen and (max-width: ${theme.breakpoints.xl}) {
				font-size: 60px;
				top: 210px;
			}
		`;
	}}
`;

const MobileCard = styled.div`
	width: 100%;
	display: block;
	padding-bottom: 45px;
	position: relative;
	margin-top: 60px;
	${(props: any) => {
		const { theme } = props;
		return css`
			background-color: ${theme.colors.tertiary.background};
		`;
	}}
`;

const MobileCardImage = styled.div`
	width: calc(100% - 20px);
	position: relative;
	top: -40px;
	margin-bottom: -40px;
`;

const MobileCardText = styled.div`
	margin-top: 25px;
	width: 100%;
	padding-left: 20px;
	padding-right: 20px;
	box-sizing: border-box;
`;

/**
 * Renders the section for both breakpoints inside its own `ParallaxProvider`. See the file header
 * for the layout and the rules a change must keep.
 */
const IgniteYourCareer: React.FC<IgniteYourCareerProps> = ({
	content,
	title1Parallax = ["70px", "0px"],
	title2Parallax = ["-70px", "0px"],
	type = "secondary",
	firstImageOnly = false,
	rightBackground,
	title1Left = "-380px",
	title2Left = "-170px",
	title1LeftSmaller = "-190px",
	title2LeftSmaller = "-50px",
	title1ParallaxSmaller = ["70px", "0px"],
	title2ParallaxSmaller = ["-70px", "0px"],
	customFontSize,
}) => {
	const {
		localization: { language },
	} = useApplication();
	const { theme } = useTheme();
	if (!content) content = IgniteYourContent[language];

	// Height of the desktop section in px: 48.6% of the page width (the left image panel's share)
	// at a 700 x 850 aspect, capped at 850 px once that width reaches 700 px.
	const [rightHeight, setRightHeight] = React.useState(850);
	// Index of the open accordion tab, which picks the image shown on the left.
	const [sectionIndex, setSectionIndex] = React.useState<number>(0);
	// The panel's background image. It trails `sectionIndex` (see the effect below), so the
	// previous image stays behind the incoming one while it slides in.
	const [currentImage, setCurrentImage] = React.useState<any>(
		content.cards[sectionIndex].desktopImage
	);
	// Measures the page width (`document.body.scrollWidth`) on mount and on every resize. The
	// resize listener is never removed.
	React.useEffect(() => {
		if (document && window) {
			const setComponentHeight = () => {
				//let outerWidth = document.body.scrollWidth;
				let windowWidth = document.body.scrollWidth;
				let sectionWidth = windowWidth * 0.486;
				let height = 0;
				let aspect = 700 / 850;
				if (sectionWidth >= 700) height = 850;
				else height = sectionWidth / aspect;
				setRightHeight(height);
			};
			window.addEventListener("resize", function () {
				setComponentHeight();
			});
			setComponentHeight();
		}
	}, []);
	// The background switches 500 ms after the tab changes, the length of the 0.5 s `top`
	// transition on the card images, so it changes once the new image has slid over it.
	React.useEffect(() => {
		if (content?.cards[sectionIndex].desktopImage !== currentImage) {
			setTimeout(() => {
				setCurrentImage(content?.cards[sectionIndex].desktopImage);
			}, 500);
		}
	}, [sectionIndex]);

	return (
		<ParallaxProvider>
			<Media greaterThanOrEqual="lg">
				<ContentSection type={type} vPadding="0" hPadding="0">
					<Contain
						css={css`
							height: ${rightHeight + "px"};
						`}
					>
						<Left
							css={css`
								height: ${rightHeight + "px"};
							`}
						>
							<ImageContainer
								css={css`
									height: ${rightHeight + "px"};
									background-image: url(${currentImage});
									background-size: cover;
								`}
							>
								{content.cards.map((card: IgniteYourCareerCard, index: number) => {
									return (
										<img
											key={index}
											css={css`
												width: 100%;
												height: auto;
												display: block;
												position: absolute;
												transition: ${firstImageOnly === true
													? "none"
													: "top ease 0.5s"};
												z-index: ${sectionIndex === index ? 1 : -1};
												top: ${sectionIndex === index ? "0px" : "100%"};
											`}
											src={card.desktopImage}
											alt={content?.title1}
										/>
									);
								})}
							</ImageContainer>
							<TitleContainer>
								<Media greaterThan="lg">
									<Parallax translateY={title1Parallax as CSSEffect}>
										<Title1
											css={css`
												left: ${title1Left};
												@media only screen and (max-width: ${theme
														.breakpoints.xl}) {
													left: ${title1LeftSmaller};
												}
												font-size: ${customFontSize
													? customFontSize
													: "100px"};
											`}
											theme={theme}
										>
											{content.title1}
										</Title1>
									</Parallax>
									<Parallax translateY={title2Parallax as CSSEffect}>
										<Title2
											css={css`
												left: ${title2Left};
												@media only screen and (max-width: ${theme
														.breakpoints.xl}) {
													left: ${title2LeftSmaller};
												}
												font-size: ${customFontSize
													? customFontSize
													: "100px"};
											`}
											theme={theme}
										>
											{content.title2}
										</Title2>
									</Parallax>
								</Media>
								<Media lessThan="xl">
									<Parallax translateY={title1ParallaxSmaller as CSSEffect}>
										<Title1
											css={css`
												left: ${title1LeftSmaller};
											`}
											theme={theme}
										>
											{content.title1}
										</Title1>
									</Parallax>
									<Parallax translateY={title2ParallaxSmaller as CSSEffect}>
										<Title2
											css={css`
												left: ${title2LeftSmaller};
											`}
											theme={theme}
										>
											{content.title2}
										</Title2>
									</Parallax>
								</Media>
							</TitleContainer>
						</Left>
						<Right
							theme={theme}
							css={css`
								height: ${rightHeight + "px"};
								background-color: ${rightBackground
									? rightBackground
									: theme.colors[type].background};
							`}
						>
							<AccordionContainer theme={theme}>
								{content.description ? (
									<Typography
										variant="div"
										css={css`
											margin-top: 0px;
											margin-bottom: 50px;
											@media (max-width: ${theme.breakpoints.xl}) {
												margin-top: 20px;
												margin-bottom: 30px;
											}
										`}
										size="md"
										type="tertiary"
									>
										{content.description}
									</Typography>
								) : (
									<></>
								)}
								<Accordion
									variant="line"
									multiple={false}
									defaultOpen={sectionIndex}
									tabClicked={(tabIndex) => {
										setSectionIndex(Number(tabIndex));
									}}
									type="tertiary"
								>
									{content &&
										content.cards.map(
											(card: IgniteYourCareerCard, index: number) => (
												<AccordionTab
													key={`tab-${index}`}
													header={card.title}
													circled={true}
													type="tertiary"
												>
													<Typography
														variant="h2"
														type="tertiary"
														css={css`
															margin-top: 52px;
															margin-bottom: 40px;
															@media (max-width: ${theme.breakpoints
																	.xl}) {
																margin-top: 5px;
																margin-bottom: 4px;
																font-size: 20px;
																${language === "fr"
																	? "margin-top:20px; margin-bottom: 20px;"
																	: ""}
															}
															${language === "fr"
																? "font-size:20px; line-height:22px;"
																: ""}
														`}
													>
														{card.title}
													</Typography>
													<Typography
														variant="p"
														size="md"
														type="tertiary"
														css={css`
															@media (max-width: ${theme.breakpoints
																	.xl}) {
																margin-top: 0px;
																margin-bottom: 5px;
																font-size: 14px;
															}
														`}
													>
														{card.description}
													</Typography>
													{card.actionLabel && card.actionUrl ? (
														<ButtonLinkContainer theme={theme}>
															<ButtonLink
																size="sm"
																label={card.actionLabel}
																to={card.actionUrl}
																type="tertiary"
															/>
														</ButtonLinkContainer>
													) : (
														<></>
													)}
												</AccordionTab>
											)
										)}
								</Accordion>
							</AccordionContainer>
						</Right>
					</Contain>
				</ContentSection>
			</Media>
			<Media lessThan="lg">
				<ContentSection vPadding="20px" hPadding="0px" type="primary">
					{content.cards.map((card: IgniteYourCareerCard, index: number) => {
						return (
							<MobileCard
								key={index}
								theme={theme}
								css={css`
									margin-bottom: ${content && index === content.cards.length - 1
										? "40px"
										: "0px"};
								`}
							>
								<MobileCardImage>
									<img
										css={css`
											width: 100%;
											height: auto;
											display: block;
											margin-left: ${index % 2 === 0 ? "0px" : "20px"};
										`}
										src={card.mobileImage}
										alt={content?.title1}
									/>
								</MobileCardImage>
								<MobileCardText>
									<Link to={card.actionUrl ?? ""}>
										<Typography
											variant="h3"
											type="tertiary"
											css={css`
												margin-bottom: 10px;
											`}
										>
											{card.title}
										</Typography>
									</Link>
									<Typography type="tertiary" size="md">
										{card.description}
									</Typography>
								</MobileCardText>
							</MobileCard>
						);
					})}
				</ContentSection>
			</Media>
		</ParallaxProvider>
	);
};

export default IgniteYourCareer;
